import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useSession } from '../auth/SessionContext';
import { homeFor } from '../nav';
import { LoadingPage, PageHeader } from '../ui';

/**
 * Route gating in the browser is a COURTESY, NOT A CONTROL.
 *
 * Everything here decides what to render; nothing here decides what may be
 * read. The API refuses an admin route with a 403 whatever the bundle
 * believes, because the bundle is public and a determined visitor can call the
 * endpoints directly. These components exist so a creator sees "you may not be
 * here" instead of a broken page full of failed requests.
 *
 * A visitor is REDIRECTED to sign in rather than shown a sign-in card in the
 * app's own chrome: the sidebar does not exist for them, so a page that renders
 * the shell with nothing in it is a worse answer than the sign-in page itself.
 * The redirect carries `?next=` with the address that was asked for, and
 * `/login` honours it, so somebody who followed a link into the app lands where
 * they were going rather than on the campaigns list.
 */

function SignIn(): React.JSX.Element {
  const { pathname, search } = useLocation();
  return <Navigate to={`/login?next=${encodeURIComponent(pathname + search)}`} replace />;
}

export function RequireSession({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { status } = useSession();

  if (status === 'loading') return <LoadingPage title="One moment" />;
  if (status === 'anonymous') return <SignIn />;
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { status, isAdmin } = useSession();

  if (status === 'loading') return <LoadingPage title="One moment" />;
  if (status === 'anonymous') return <SignIn />;
  if (!isAdmin) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Not your area" />
        <div className="alert" data-tone="critical" role="alert">
          <strong>This is the operator area.</strong>
          <span>
            Your account is a creator account, so there is nothing here for you. Everything you can
            do is under Campaigns, Submit and Wallet.
          </span>
        </div>
        <div className="actions">
          <Link className="btn btn-ghost" to="/campaigns">
            Back to campaigns
          </Link>
        </div>
      </>
    );
  }
  return <>{children}</>;
}

/**
 * The other direction: the front door and the sign-in page are for people who
 * are not signed in. Somebody who is goes to their own first page — a creator
 * to Campaigns, an operator to the Overview — or to whatever `?next=` sent them
 * here to reach, which is what makes a bookmarked deep link work on a machine
 * that is still signed in.
 */
export function SignedOutOnly({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { status, isAdmin } = useSession();
  const [params] = useSearchParams();
  const next = params.get('next');

  /* No spinner here, unlike the guards above. This is the front door and the
     sign-in form: the common case is a visitor with no session at all, and
     making everybody watch a placeholder while the API confirms what the
     absence of a cookie already said is a slower door for the sake of a rarer
     case. Somebody who IS signed in is redirected the moment the answer
     lands. */
  if (status === 'authenticated') {
    /* Only an in-app address, and never back to a page that would bounce
       straight back here: `next` arrives from the query string, which anybody
       can write. */
    const target = next && next.startsWith('/') && !next.startsWith('//') ? next : homeFor(isAdmin);
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}
